import { useState, useEffect } from "react";
import toast from "../../../../utils/toast";
import {
  getPaymentModeList,
  getPaymentModeById,
  createUpdatePaymentModeById,
  togglePaymentModeById,
  removePaymentModeById,
  createUpdatePaymentModeFields,
  removePaymentModeFieldsById,
} from "../../../../services/admin/adminServices";

export default function PaymentModes() {
  const [modesList, setModesList] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // Add Mode Modal
  const [isModalOpenAddMode, setIsModalOpenAddMode] = useState(false);
  const [addModeForm, setAddModeForm] = useState({
    name: "",
    icon: "",
    description: "",
    isEnabled: true,
    sortOrder: 0,
  });
  const [addModeLoading, setAddModeLoading] = useState(false);
  const [addModeErrors, setAddModeErrors] = useState({});

  const ACCEPTED_VALUE_TYPES = [
    { value: "any", label: "Any characters" },
    { value: "numeric", label: "Numeric only (0-9)" },
    { value: "alpha", label: "Alphabets only (A-Z, a-z)" },
    { value: "alphanumeric", label: "Alphanumeric (A-Z, 0-9)" },
    { value: "email", label: "Email format" },
    { value: "phone", label: "Phone number" },
    { value: "url", label: "URL / Link" },
  ];

  // Edit Mode Modal
  const [isModalOpenEditMode, setIsModalOpenEditMode] = useState(false);
  const [editModeForm, setEditModeForm] = useState({});
  const [editModeLoading, setEditModeLoading] = useState(false);
  const [editModeErrors, setEditModeErrors] = useState({});
  const [selectedModeId, setSelectedModeId] = useState(null);

  // Delete Mode Modal
  const [isModalOpenDeleteMode, setIsModalOpenDeleteMode] = useState(false);
  const [deleteModeId, setDeleteModeId] = useState(null);

  // Add Field Modal
  const [isModalOpenAddField, setIsModalOpenAddField] = useState(false);
  const [addFieldForm, setAddFieldForm] = useState({
    label: "",
    fieldType: "text",
    acceptedValueType: "any",
    placeholder: "",
    isRequired: true,
    sortOrder: 0,
  });
  const [addFieldModeId, setAddFieldModeId] = useState(null);
  const [addFieldLoading, setAddFieldLoading] = useState(false);
  const [addFieldErrors, setAddFieldErrors] = useState({});

  // Edit Field Modal
  const [isModalOpenEditField, setIsModalOpenEditField] = useState(false);
  const [editFieldForm, setEditFieldForm] = useState({});
  const [editFieldModeId, setEditFieldModeId] = useState(null);
  const [editFieldId, setEditFieldId] = useState(null);
  const [editFieldLoading, setEditFieldLoading] = useState(false);
  const [editFieldErrors, setEditFieldErrors] = useState({});

  // Delete Field Modal
  const [isModalOpenDeleteField, setIsModalOpenDeleteField] = useState(false);
  const [deleteFieldId, setDeleteFieldId] = useState(null);

  const FIELD_TYPES = [
    { value: "text", label: "Text Input" },
    { value: "number", label: "Number Input" },
    { value: "textarea", label: "Text Area (multiline)" },
    { value: "file", label: "File Upload" },
    { value: "date", label: "Date Picker" },
    { value: "select", label: "Dropdown Select" },
  ];

  useEffect(() => {
    fetchModesList();
  }, []);

  const fetchModesList = async () => {
    setListLoading(true);
    try {
      const res = await getPaymentModeList();
      if (res.statusCode === 1) setModesList(res.data);
    } catch (err) {
      console.log("Failed to fetch payment modes");
    } finally {
      setListLoading(false);
    }
  };

  // ── Toggle Mode ──
  const handleToggleMode = async (mode) => {
    const newValue = !mode.isEnabled;
    setModesList((prev) =>
      prev.map((m) =>
        m.modeId === mode.modeId ? { ...m, isEnabled: newValue } : m,
      ),
    );
    try {
      const res = await togglePaymentModeById(mode.modeId, newValue);
      if (res.statusCode === 1) toast.success(res.message);
      else {
        toast.error(res.message);
        setModesList((prev) =>
          prev.map((m) => (m.modeId === mode.modeId ? mode : m)),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setModesList((prev) =>
        prev.map((m) => (m.modeId === mode.modeId ? mode : m)),
      );
    }
  };

  // ── Add Mode ──
  const openAddModeModal = () => {
    setAddModeForm({
      name: "",
      icon: "",
      description: "",
      isEnabled: true,
      sortOrder: 0,
    });
    setAddModeErrors({});
    setIsModalOpenAddMode(true);
  };

  const closeAddModeModal = () => {
    setIsModalOpenAddMode(false);
    setAddModeForm({
      name: "",
      icon: "",
      description: "",
      isEnabled: true,
      sortOrder: 0,
    });
    setAddModeErrors({});
  };

  const handleAddModeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddModeForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setAddModeErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateMode = async () => {
    const e = {};
    if (!addModeForm.name) e.name = "Mode name is required.";
    if (Object.keys(e).length) {
      setAddModeErrors(e);
      return;
    }
    setAddModeLoading(true);
    try {
      const res = await createUpdatePaymentModeById({
        modeId: 0,
        ...addModeForm,
        sortOrder: Number(addModeForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddModeModal();
        fetchModesList();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddModeLoading(false);
    }
  };

  // ── Edit Mode ──
  const openEditModeModal = async (id) => {
    try {
      const res = await getPaymentModeById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditModeForm({
          name: d.name || "",
          icon: d.icon || "",
          description: d.description || "",
          isEnabled: d.isEnabled,
          sortOrder: d.sortOrder || 0,
        });
        setSelectedModeId(id);
        setEditModeErrors({});
        setIsModalOpenEditMode(true);
      }
    } catch (err) {
      toast.error("Failed to load payment mode.");
    }
  };

  const closeEditModeModal = () => {
    setIsModalOpenEditMode(false);
    setEditModeForm({});
    setEditModeErrors({});
    setSelectedModeId(null);
  };

  const handleEditModeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditModeForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditModeErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdateMode = async () => {
    const e = {};
    if (!editModeForm.name) e.name = "Mode name is required.";
    if (Object.keys(e).length) {
      setEditModeErrors(e);
      return;
    }
    setEditModeLoading(true);
    try {
      const res = await createUpdatePaymentModeById({
        modeId: selectedModeId,
        ...editModeForm,
        sortOrder: Number(editModeForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModeModal();
        fetchModesList();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditModeLoading(false);
    }
  };

  // ── Delete Mode ──
  const confirmDeleteMode = async () => {
    try {
      const res = await removePaymentModeById(deleteModeId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenDeleteMode(false);
        setDeleteModeId(null);
        fetchModesList();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  // ── Add Field ──
  const openAddFieldModal = (modeId) => {
    setAddFieldForm({
      label: "",
      fieldType: "text",
      placeholder: "",
      isRequired: true,
      sortOrder: 0,
    });
    setAddFieldModeId(modeId);
    setAddFieldErrors({});
    setIsModalOpenAddField(true);
  };

  const closeAddFieldModal = () => {
    setIsModalOpenAddField(false);
    setAddFieldForm({
      label: "",
      fieldType: "text",
      placeholder: "",
      isRequired: true,
      sortOrder: 0,
    });
    setAddFieldErrors({});
    setAddFieldModeId(null);
  };

  const handleAddFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddFieldForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setAddFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateField = async () => {
    const e = {};
    if (!addFieldForm.label) e.label = "Field label is required.";
    if (Object.keys(e).length) {
      setAddFieldErrors(e);
      return;
    }
    setAddFieldLoading(true);
    try {
      const res = await createUpdatePaymentModeFields({
        fieldId: 0,
        acceptedValueType: addFieldForm.acceptedValueType,
        modeId: addFieldModeId,
        ...addFieldForm,
        sortOrder: Number(addFieldForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddFieldModal();
        fetchModesList();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddFieldLoading(false);
    }
  };

  // ── Edit Field ──
  const openEditFieldModal = (field, modeId) => {
    setEditFieldForm({
      label: field.label || "",
      fieldType: field.fieldType || "text",
      acceptedValueType: field.acceptedValueType || "any",
      placeholder: field.placeholder || "",
      isRequired: field.isRequired,
      sortOrder: field.sortOrder || 0,
    });
    setEditFieldModeId(modeId);
    setEditFieldId(field.fieldId);
    setEditFieldErrors({});
    setIsModalOpenEditField(true);
  };

  const closeEditFieldModal = () => {
    setIsModalOpenEditField(false);
    setEditFieldForm({});
    setEditFieldErrors({});
    setEditFieldId(null);
    setEditFieldModeId(null);
  };

  const handleEditFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFieldForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdateField = async () => {
    const e = {};
    if (!editFieldForm.label) e.label = "Field label is required.";
    if (Object.keys(e).length) {
      setEditFieldErrors(e);
      return;
    }
    setEditFieldLoading(true);
    try {
      const res = await createUpdatePaymentModeFields({
        fieldId: editFieldId,
        modeId: editFieldModeId,
        ...editFieldForm,
        sortOrder: Number(editFieldForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditFieldModal();
        fetchModesList();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditFieldLoading(false);
    }
  };

  // ── Delete Field ──
  const confirmDeleteField = async () => {
    try {
      const res = await removePaymentModeFieldsById(deleteFieldId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenDeleteField(false);
        setDeleteFieldId(null);
        fetchModesList();
      } else toast.error(res.message || "Failed to delete field.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Payment Mode Settings</div>
            <div className="page-sub">
              System › Configure payment modes &amp; custom fields
            </div>
          </div>
          <div className="topbar-right">
            <button className="btn btn-primary" onClick={openAddModeModal}>
              + Add Payment Mode
            </button>
          </div>
        </div>

        <div className="content">
          <div className="info-box mb16">
            <strong>Payment Modes</strong> — define the types of payment your
            platform accepts (Bank Transfer, UPI, QR Code, etc.) and configure
            what input fields payers must fill. To add your actual payment
            account details (bank account numbers, UPI IDs, QR codes), go to{" "}
            <strong>Payment Instruments</strong>.
          </div>

          {listLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card mb16">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {Array.from({ length: 4 }).map((_, j) => (
                    <div
                      key={j}
                      style={{
                        height: 14,
                        borderRadius: 6,
                        background:
                          "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        width: j === 0 ? "40%" : "70%",
                      }}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : modesList.length === 0 ? (
            <div
              className="card"
              style={{
                textAlign: "center",
                color: "var(--text-tertiary)",
                padding: 24,
              }}
            >
              No payment modes configured.
            </div>
          ) : (
            modesList.map((mode) => (
              <div
                key={mode.modeId}
                className="card mb16"
                style={{ borderLeft: "4px solid var(--teal)" }}
              >
                {/* Mode Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <span style={{ fontSize: 26 }}>{mode.icon}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {mode.name}
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        {mode.description}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={mode.isEnabled}
                        onChange={() => handleToggleMode(mode)}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <button
                      className="btn btn-sm"
                      onClick={() => openEditModeModal(mode.modeId)}
                    >
                      ✎ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => openAddFieldModal(mode.modeId)}
                    >
                      + Add Field
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        setDeleteModeId(mode.modeId);
                        setIsModalOpenDeleteMode(true);
                      }}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>

                {/* Fields Table */}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 8,
                  }}
                >
                  Input Fields (collected from payer)
                </div>

                {mode.fields.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                      padding: "8px 0",
                    }}
                  >
                    No fields added. Click "+ Add Field" to add.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ minWidth: 700 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Field Label</th>
                          <th>Field Type</th>
                          <th>Value Type</th>
                          <th>Required</th>
                          <th>Placeholder</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mode.fields.map((field, index) => (
                          <tr key={field.fieldId}>
                            <td className="sn-col">{index + 1}</td>
                            <td style={{ fontWeight: 500 }}>
                              {field.label}
                              {field.isRequired && (
                                <span
                                  style={{ color: "var(--red)", marginLeft: 3 }}
                                >
                                  *
                                </span>
                              )}
                            </td>
                            <td>
                              <span className="badge badge-info">
                                {field.fieldType}
                              </span>
                            </td>
                            <td>
                              <span
                                className="badge badge-gray"
                                style={{ fontSize: 10 }}
                              >
                                {ACCEPTED_VALUE_TYPES.find(
                                  (v) => v.value === field.acceptedValueType,
                                )?.label ||
                                  field.acceptedValueType ||
                                  "—"}
                              </span>
                            </td>
                            <td>
                              {field.isRequired ? (
                                <span className="badge badge-active">
                                  Required
                                </span>
                              ) : (
                                <span className="badge badge-gray">
                                  Optional
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                fontSize: 11,
                                color: "var(--text-tertiary)",
                              }}
                            >
                              {field.placeholder}
                            </td>
                            <td>
                              <div className="td-actions">
                                <span
                                  className="action-icon"
                                  title="Edit"
                                  onClick={() =>
                                    openEditFieldModal(field, mode.modeId)
                                  }
                                >
                                  <span className="tip">Edit</span>✎
                                </span>
                                <span
                                  className="action-icon red"
                                  title="Delete"
                                  onClick={() => {
                                    setDeleteFieldId(field.fieldId);
                                    setIsModalOpenDeleteField(true);
                                  }}
                                >
                                  <span className="tip">Delete</span>🗑
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Add Mode Modal ── */}
      {isModalOpenAddMode && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">Add New Payment Mode</div>
              <button className="modal-close" onClick={closeAddModeModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Payment Mode Name *</label>
                <input
                  type="text"
                  name="name"
                  value={addModeForm.name}
                  onChange={handleAddModeChange}
                  placeholder="e.g. Bank Transfer, Cheque, Cash Deposit…"
                  style={addModeErrors.name ? { borderColor: "#dc2626" } : {}}
                />
                {addModeErrors.name && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addModeErrors.name}
                  </span>
                )}
              </div>
              <div className="form-grid">
                <div className="form-group mb12">
                  <label>Icon / Emoji</label>
                  <input
                    type="text"
                    name="icon"
                    value={addModeForm.icon}
                    onChange={handleAddModeChange}
                    placeholder="e.g. 🏦 💳 📱 📷"
                  />
                  <div className="input-hint">
                    Paste any emoji as the mode icon
                  </div>
                </div>
                <div className="form-group mb12">
                  <label>Status</label>
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isEnabled"
                        checked={addModeForm.isEnabled}
                        onChange={handleAddModeChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <span
                      style={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                      Enabled
                    </span>
                  </div>
                </div>
              </div>
              <div className="form-group mb12">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  value={addModeForm.description}
                  onChange={handleAddModeChange}
                  placeholder="Brief description shown to users"
                />
              </div>
              <div className="info-box" style={{ fontSize: 11 }}>
                After creating the mode, add custom input fields using the "+
                Add Field" button on the mode card.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModeModal}
                disabled={addModeLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateMode}
                disabled={addModeLoading}
              >
                {addModeLoading ? "Saving..." : "Save Payment Mode"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpenEditMode && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">Edit Payment Mode</div>
              <button className="modal-close" onClick={closeEditModeModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Payment Mode Name *</label>
                <input
                  type="text"
                  name="name"
                  value={editModeForm.name}
                  onChange={handleEditModeChange}
                  placeholder="e.g. Bank Transfer, Cheque, Cash Deposit…"
                  style={editModeErrors.name ? { borderColor: "#dc2626" } : {}}
                />
                {editModeErrors.name && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {editModeErrors.name}
                  </span>
                )}
              </div>
              <div className="form-grid">
                <div className="form-group mb12">
                  <label>Icon / Emoji</label>
                  <input
                    type="text"
                    name="icon"
                    value={editModeForm.icon}
                    onChange={handleEditModeChange}
                    placeholder="e.g. 🏦 💳 📱 📷"
                  />
                  <div className="input-hint">
                    Paste any emoji as the mode icon
                  </div>
                </div>
                <div className="form-group mb12">
                  <label>Status</label>
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isEnabled"
                        checked={editModeForm.isEnabled}
                        onChange={handleEditModeChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <span
                      style={{ fontSize: 12, color: "var(--text-secondary)" }}
                    >
                      Enabled
                    </span>
                  </div>
                </div>
              </div>
              <div className="form-group mb12">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  value={editModeForm.description}
                  onChange={handleEditModeChange}
                  placeholder="Brief description shown to users"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModeModal}
                disabled={editModeLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateMode}
                disabled={editModeLoading}
              >
                {editModeLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpenDeleteMode && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Confirm Delete</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenDeleteMode(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "var(--text-primary)" }}>
                Are you sure you want to delete this payment mode? All
                associated fields will also be deleted. This action cannot be
                undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenDeleteMode(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteMode}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Field Modal ── */}
      {isModalOpenAddField && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">
              <div className="modal-title">Add Field</div>
              <button className="modal-close" onClick={closeAddFieldModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Field Label *</label>
                <input
                  type="text"
                  name="label"
                  value={addFieldForm.label}
                  onChange={handleAddFieldChange}
                  placeholder="e.g. Account Number, IFSC Code, UPI ID…"
                  style={addFieldErrors.label ? { borderColor: "#dc2626" } : {}}
                />
                {addFieldErrors.label && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addFieldErrors.label}
                  </span>
                )}
                <div className="input-hint">
                  This label is shown to users above the input field
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group mb12">
                  <label>Field Type *</label>
                  <select
                    name="fieldType"
                    value={addFieldForm.fieldType}
                    onChange={handleAddFieldChange}
                  >
                    {FIELD_TYPES.map((ft) => (
                      <option key={ft.value} value={ft.value}>
                        {ft.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb12">
                  <label>Accepted Value Type *</label>
                  <select
                    name="acceptedValueType"
                    value={addFieldForm.acceptedValueType}
                    onChange={handleAddFieldChange}
                  > 
                    {ACCEPTED_VALUE_TYPES.map((vt) => (
                      <option key={vt.value} value={vt.value}>
                        {vt.label}
                      </option>
                    ))}
                  </select>
                  <div className="input-hint">
                    Validation applied when the user submits the form
                  </div>
                </div>
                <div className="form-group mb12">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={addFieldForm.sortOrder}
                    onChange={handleAddFieldChange}
                    min={0}
                  />
                </div>
              </div>
              <div className="form-group mb12">
                <label>Placeholder Text</label>
                <input
                  type="text"
                  name="placeholder"
                  value={addFieldForm.placeholder}
                  onChange={handleAddFieldChange}
                  placeholder="Hint shown inside the input"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <label className="toggle">
                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={addFieldForm.isRequired}
                    onChange={handleAddFieldChange}
                  />
                  <span className="toggle-slider" />
                </label>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    Required Field
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                    User must fill this field before submitting
                  </div>
                </div>
              </div>
              {/* Preview */}
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  border: "0.5px solid var(--border-mid)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--bg-primary)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 10,
                  }}
                >
                  Field Preview
                </div>
                <div className="form-group">
                  <label
                    style={{ fontSize: 12, color: "var(--text-secondary)" }}
                  >
                    {addFieldForm.label || "Field Label"}
                    {addFieldForm.isRequired && " *"}
                  </label>
                  <input
                    type="text"
                    placeholder={addFieldForm.placeholder || "Placeholder text"}
                    style={{ pointerEvents: "none" }}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddFieldModal}
                disabled={addFieldLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateField}
                disabled={addFieldLoading}
              >
                {addFieldLoading ? "Saving..." : "Save Field"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpenEditField && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">
              <div className="modal-title">Edit Field</div>
              <button className="modal-close" onClick={closeEditFieldModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Field Label *</label>
                <input
                  type="text"
                  name="label"
                  value={editFieldForm.label}
                  onChange={handleEditFieldChange}
                  placeholder="e.g. Account Number, IFSC Code, UPI ID…"
                  style={
                    editFieldErrors.label ? { borderColor: "#dc2626" } : {}
                  }
                />
                {editFieldErrors.label && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {editFieldErrors.label}
                  </span>
                )}
                <div className="input-hint">
                  This label is shown to users above the input field
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group mb12">
                  <label>Field Type *</label>
                  <select
                    name="fieldType"
                    value={editFieldForm.fieldType}
                    onChange={handleEditFieldChange}
                  >
                    {FIELD_TYPES.map((ft) => (
                      <option key={ft.value} value={ft.value}>
                        {ft.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb12">
                  <label>Accepted Value Type *</label>
                  <select
                    name="acceptedValueType"
                    value={addFieldForm.acceptedValueType}
                    onChange={handleAddFieldChange}
                  >
                    {ACCEPTED_VALUE_TYPES.map((vt) => (
                      <option key={vt.value} value={vt.value}>
                        {vt.label}
                      </option>
                    ))}
                  </select>
                  <div className="input-hint">
                    Validation applied when the user submits the form
                  </div>
                </div>
                <div className="form-group mb12">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={editFieldForm.sortOrder}
                    onChange={handleEditFieldChange}
                    min={0}
                  />
                </div>
              </div>
              <div className="form-group mb12">
                <label>Placeholder Text</label>
                <input
                  type="text"
                  name="placeholder"
                  value={editFieldForm.placeholder}
                  onChange={handleEditFieldChange}
                  placeholder="Hint shown inside the input"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <label className="toggle">
                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={editFieldForm.isRequired}
                    onChange={handleEditFieldChange}
                  />
                  <span className="toggle-slider" />
                </label>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    Required Field
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                    User must fill this field before submitting
                  </div>
                </div>
              </div>
              {/* Preview */}
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  border: "0.5px solid var(--border-mid)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--bg-primary)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 10,
                  }}
                >
                  Field Preview
                </div>
                <div className="form-group">
                  <label
                    style={{ fontSize: 12, color: "var(--text-secondary)" }}
                  >
                    {editFieldForm.label || "Field Label"}
                    {editFieldForm.isRequired && " *"}
                  </label>
                  <input
                    type="text"
                    placeholder={
                      editFieldForm.placeholder || "Placeholder text"
                    }
                    style={{ pointerEvents: "none" }}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditFieldModal}
                disabled={editFieldLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateField}
                disabled={editFieldLoading}
              >
                {editFieldLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpenDeleteField && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Confirm Delete</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenDeleteField(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "var(--text-primary)" }}>
                Are you sure you want to delete this field? This action cannot
                be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenDeleteField(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteField}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
